"use client";

import React, { useMemo, useState } from "react";
import { Row, Col, Card, Button, Form } from "react-bootstrap";
import Image from "next/image";
import Link from "next/link";
import styles from "../page.module.css";

type Tool = {
  name: string;
  slug: string;
  desc: string;
  link: string;
  logo: string;
  category: string | string[];
};

export default function ToolsClient({
  initialTools,
}: {
  initialTools: Tool[];
}) {
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("All");

  const categories = useMemo(
    () => [
      "All",
      "AI Tools",
      "Remote Work",
      "Productivity",
      "Project Management",
      "Digital Assets",
      "Developer Units",
      "Design",
      "Video Creation",
    ],
    []
  );

  const filteredTools = useMemo(() => {
    const q = search.trim().toLowerCase();
    return initialTools.filter((tool) => {
      const categoriesArray = Array.isArray(tool.category)
        ? tool.category.filter(Boolean)
        : [tool.category].filter(Boolean);
      const matchesCategory =
        category === "All" || categoriesArray.includes(category);

      if (!matchesCategory) return false;
      if (q === "") return true;

      return (
        tool.name.toLowerCase().includes(q) ||
        categoriesArray.some((cat) => cat.toLowerCase().includes(q))
      );
    });
  }, [initialTools, category, search]);

  return (
    <>
      <div className="ai-tools-description">
        <h4>The Best AI Tools to Supercharge Your Workflow:</h4>
        <p className="text-justify">
          Welcome to AIToolPilot — your smart co-pilot in the AI era! Explore
          over 100 handpicked AI tools designed to boost productivity,
          streamline remote work, manage projects efficiently, enhance design
          and video creation, and provide cutting-edge digital assets. Whether
          you’re a developer, designer, or entrepreneur, our curated collection
          helps you save time, automate repetitive tasks, and unlock creative
          potential. Filter tools by category, type, or popularity, and find the
          perfect AI solution to elevate your workflow today.
        </p>
        <p className="text-justify text-success">
          Tech-savvy users love simple web apps that save time, help find the
          right AI tools, and automate repetitive tasks to boost productivity.
          Click any tool card or logo to explore its key features, see if it
          fits your workflow, and visit the official site. Explore tools that
          make your workflow smarter, faster, and easier.
        </p>
      </div>

      {/* Category buttons */}
      <div className={styles.categoryScrollWrapper}>
        <div className={styles.categoryButtons}>
          {categories.map((cat) => (
            <Button
              key={cat}
              variant={cat === category ? "dark" : "outline-dark"}
              onClick={() => setCategory(cat)}
              aria-pressed={cat === category}
            >
              {cat}
            </Button>
          ))}
        </div>
      </div>

      {/* Search bar */}
      <Form.Group
        controlId="searchTools"
        className="mb-3 mt-3"
        style={{ maxWidth: "91%", margin: "auto" }}
      >
        <Form.Label className="visually-hidden">Search tools</Form.Label>
        <Form.Control
          type="search"
          placeholder="Search tools..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search tools"
          className="w-100"
        />
      </Form.Group>

      {/* Tools grid */}
      <Row className="mt-4">
        {filteredTools.map((tool, index) => (
          <Col md={4} className="mb-4" key={index}>
            <Card
              className={`${styles.toolCard} shadow-sm border-0 rounded-3 h-100`}
            >
              <Card.Body className="d-flex flex-column align-items-center p-4 cursor-pointer">
                {/* Main link wrapping logo + title */}
                <Link
                  href={`/tools/${tool.slug}`}
                  className="text-decoration-none w-100"
                >
                  <div className="logo-wrapper mb-3 d-flex justify-content-center align-items-center w-100">
                    <Image
                      src={tool.logo}
                      alt={tool.name}
                      width={100}
                      height={50}
                      className="logo-img"
                      style={{ objectFit: "contain" }}
                      loading="lazy"
                      unoptimized
                    />
                  </div>
                  <h5 className="text-center text-dark">{tool.name}</h5>
                </Link>

                <p className="text-muted small text-center mt-2">{tool.desc}</p>

                <Button
                  href={tool.link}
                  variant="dark"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-100 mt-auto"
                >
                  Visit Tool
                </Button>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {filteredTools.length === 0 && (
        <p className="text-center text-muted mt-4">No tools found.</p>
      )}
    </>
  );
}
